<template>
  <div class="auth-view">
    <div class="_container">
      <h1>
        {{ title }}
      </h1>
      <c-button
        outlined
        has-shadow
        stretch
        icon="GoogleColor"
        :loading="googleBtnLoading"
        :style="{ fontWeight: '500' }"
        @click="handleGoogleSubmit"
      >
        Continue with Google
      </c-button>
      <c-divider />
      <c-form-field label="Email">
        <c-input
          v-model:value="email"
          autocomplete="email"
          placeholder="Enter your email address..."
          show-border
          type="email"
          show-background
          @keydown.enter="handleEmailSubmit"
        />
      </c-form-field>
      <c-form-field label="Password">
        <c-input
          v-model:value="password"
          placeholder="Enter a password..."
          show-border
          show-background
          type="password"
          @keydown.enter="handleEmailSubmit"
        />
      </c-form-field>
      <c-button
        outlined
        color="primary"
        :loading="emailLoading"
        has-shadow
        stretch
        :style="{ marginTop: '1.4rem', fontWeight: '500' }"
        @click="handleEmailSubmit"
      >
        Continue with email
      </c-button>
      <div
        v-if="errorMessage !== ''"
        class="_error-message"
      >
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import CButton from 'shared/components/CButton.vue';
import CDivider from 'shared/components/CDivider.vue';
import CFormField from 'shared/components/CFormField.vue';
import CInput from 'shared/components/CInput.vue';
import { createUserWithEmailAndPassword,
  doc,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  httpsCallable,
  sendEmailVerification,
  setDoc, signInWithEmailAndPassword, signInWithPopup } from 'shared/services/firebase';

export default {
  name: 'AuthView',
  props: {
    title: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      default: 'register',
    },
    context: {
      type: String,
      default: 'browser',
    },
  },
  components: { CFormField, CInput, CButton, CDivider },
  data() {
    return {
      emailLoading: false,
      email: '',
      password: '',
      user: null,
      errorMessage: '',
      googleBtnLoading: false,
    };
  },
  computed: {
    emailFn() {
      if (this.mode === 'register') {
        return createUserWithEmailAndPassword;
      }
      return signInWithEmailAndPassword;
    },
  },
  methods: {
    redirect() {
      if (this.context === 'sidepanel') {
        this.$router.push('/sidepanel');
        return;
      }
      this.$router.push('/');
    },
    async handleEmailSubmit() {
      this.emailLoading = true;
      this.emailFn(this.$auth, this.email, this.password)
        .then(async (userCredential) => {
          this.user = userCredential.user;
          const token = await httpsCallable(this.$functions, 'createCustomToken')(this.user.uid);
          if (this.$browser.runtime !== undefined) {
            this.$browser.runtime.sendMessage(import.meta.env.VITE_EXTENSION_ID, { token });
          }

          if (this.mode === 'register') {
            sendEmailVerification(this.user);
            try {
              await setDoc(
                doc(this.$db, 'users', userCredential.user.uid),
                {
                  uid: userCredential.user.uid,
                  email: userCredential.user.email,
                  displayName: userCredential.user.displayName,
                  photoURL: userCredential.user.photoURL,
                },
                { merge: true },
              );
              this.redirect();
            } catch (err) {
              console.error(err);
            }
            return;
          }
          this.redirect();
        })
        .catch((error) => {
          console.log(error);
          switch (error.errorMessage) {
            case 'Firebase: Error (auth/invalid-email)':
              this.errorMessage = 'Email is invalid.';
              break;
            default:
              this.errorMessage = 'An error occurred while registering.';
          }
        }).finally(() => {
          this.emailLoading = false;
        });
    },
    async handleGoogleSubmit() {
      this.googleBtnLoading = true;
      const provider = new GoogleAuthProvider();
      signInWithPopup(this.$auth, provider)
        .then(async (result) => {
          this.user = result.user;
          const token = await httpsCallable(this.$functions, 'createCustomToken')(this.user.uid);
          if (this.$browser.runtime !== undefined) {
            this.$browser.runtime.sendMessage(import.meta.env.VITE_EXTENSION_ID, { token });
          }
          const { isNewUser } = getAdditionalUserInfo(result);

          if (!isNewUser) {
            this.redirect();
            return;
          }
          await setDoc(
            doc(this.$db, 'users', result.user.uid),
            {
              uid: result.user.uid,
              email: result.user.email,
              displayName: result.user.displayName,
              photoURL: result.user.photoURL,
            },
            { merge: true },
          );
          this.redirect();
        })
        .catch((error) => {
          this.errorMessage = error.message;
        }).finally(() => {
          this.googleBtnLoading = false;
        });
    },
  },
  watch: {
    email() {
      this.errorMessage = '';
    },
  },
};
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>
.auth-view {
  ._container {
    max-width: 300px;
    padding: 0 4rem;
    margin: 20vh auto 10vh;

    h1 {
      text-align: center;
    }
  }

  ._error-message {
    padding: 1rem;
    color: map_get($red, "base");
    text-align: center;
  }
}
</style>
